/**
 * Finds the appropriate layer ID to insert surround (positron) layers before.
 *
 * Surround layers replace the venue's own roads, railways, and outdoor areas
 * (which are hidden via conflictingLayers.js).  They must render at the same
 * z-depth those venue layers originally occupied — below venue structures like
 * concrete, curbs, buildings, and labels.
 *
 * Strategy: return the ID of the **first** sole-conflicting layer in the style.
 * Since conflicting layers are hidden, inserting before the first one places
 * surround layers at exactly the right depth:
 *
 *  - base/cdc/llia/testvmsgso: first conflicting is `outdoor - GCL`[2]
 *  - ewruato:                   first conflicting is `roads`[4]
 *  - lhr/lhrar/lhrja:           first conflicting is `road polygons`[2]
 *
 * Fallback: undefined (append to end — MapLibre default).
 */

import type { StyleDefinition } from '../types.ts';
import { extractCategories, isConflictingLayer } from './conflictingLayers.ts';

const LABEL_CATEGORIES = new Set(['label', 'label.building', 'label.floor', 'label.section', 'dynamicLabel']);

/**
 * @param {{ layers: object[] }} style - A Mapbox GL style object
 * @returns {string|undefined} The layer ID to insert before, or undefined
 */
export const findSurroundInsertionPoint = (style: StyleDefinition): string | undefined => {
  for (let i = 0; i < style.layers.length; i++) {
    if (isConflictingLayer(style.layers[i])) {
      return style.layers[i].id;
    }
  }
  return undefined;
};

/**
 * @param {{ layers: object[] }} style - A Mapbox GL style object
 * @returns {string|undefined} The first venue label layer ID, or undefined to append above all layers
 */
export const findSurroundLabelInsertionPoint = (style: StyleDefinition): string | undefined => {
  for (let i = 0; i < style.layers.length; i++) {
    const categories = extractCategories(style.layers[i].filter);
    if (categories.some(category => LABEL_CATEGORIES.has(category))) {
      return style.layers[i].id;
    }
  }
  return undefined;
};
